<template>
  <Head title="Team Members" />

  <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Team Members</h1>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage your team members and their roles
          </p>
        </div>
        <div class="flex gap-3">
          <Link :href="route('team.roles.index')">
            <Button variant="secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              View System Roles
            </Button>
          </Link>
          <Button v-if="canInviteUsers" @click="openInviteModal" :disabled="isAtLimit">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Invite Member
          </Button>
        </div>
      </div>

      <!-- Usage Warning -->
      <Alert v-if="usagePercentage >= 90 && usageLimit !== 'unlimited'" variant="warning">
        <template #icon>
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        </template>
        <template #title>Approaching User Limit</template>
        <div class="flex items-center justify-between">
          <p>
            You're using {{ usageCurrent }} of {{ usageLimit }} team member slots.
            <Link :href="route('settings.billing.index')" class="underline font-medium ml-1">
              Upgrade your plan
            </Link>
            to add more members.
          </p>
        </div>
      </Alert>

      <!-- At Limit Warning -->
      <Alert v-if="isAtLimit" variant="danger">
        <template #icon>
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </template>
        <template #title>User Limit Reached</template>
        <div class="flex items-center justify-between">
          <p>
            You've reached your maximum of {{ usageLimit }} team members. Please upgrade your plan to invite more members.
          </p>
          <Link :href="route('settings.billing.index')">
            <Button size="sm" variant="secondary">
              Upgrade Plan
            </Button>
          </Link>
        </div>
      </Alert>

      <!-- Search and Filters -->
      <Card>
        <div class="flex flex-col sm:flex-row gap-4">
          <!-- Search Input -->
          <div class="flex-1">
            <div class="relative">
              <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search by name, email, or role..."
                class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                @input="handleSearch"
              />
              <button
                v-if="searchQuery"
                @click="clearSearch"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Status Filter -->
          <div class="sm:w-48">
            <select
              v-model="statusFilter"
              @change="handleFilter"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="invited">Invited</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </Card>

      <!-- Team Members Table -->
      <Card>
        <template #header>
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
              Active Members
            </h3>
            <Badge variant="secondary">{{ members.length }}</Badge>
          </div>
        </template>
        <div class="overflow-x-auto">
          <table class="w-full" v-if="members && members.length > 0">
            <thead class="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Member
                </th>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Roles
                </th>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Last Accessed
                </th>
                <th class="text-right py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr
                v-for="member in members"
                :key="member.id"
                class="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <td class="py-4 px-6">
                  <div class="flex items-center space-x-3">
                    <Avatar
                      :src="member.profile_photo_url"
                      :name="member.full_name"
                      size="md"
                    />
                    <div>
                      <p class="font-medium text-gray-900 dark:text-gray-100">
                        {{ member.full_name }}
                        <Badge v-if="member.is_you" variant="info" size="sm" class="ml-2">You</Badge>
                      </p>
                      <p class="text-sm text-gray-600 dark:text-gray-400">{{ member.email }}</p>
                    </div>
                  </div>
                </td>
                <td class="py-4 px-6">
                  <div class="flex flex-wrap gap-1">
                    <Badge
                      v-for="role in member.roles"
                      :key="role"
                      size="sm"
                      :variant="getRoleVariant(role)"
                    >
                      {{ formatRole(role) }}
                    </Badge>
                  </div>
                </td>
                <td class="py-4 px-6">
                  <Badge :variant="getStatusVariant(member.status)">
                    {{ formatStatus(member.status) }}
                  </Badge>
                </td>
                <td class="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                  {{ formatRelativeDate(member.last_accessed_at) }}
                </td>
                <td class="py-4 px-6">
                  <div class="flex justify-end gap-2">
                    <Button
                      v-if="canManageUsers"
                      size="sm"
                      variant="ghost"
                      @click="editMemberRoles(member)"
                      :disabled="member.is_subscriber || member.is_you"
                      :title="member.is_subscriber ? 'Cannot edit subscriber roles' : (member.is_you ? 'Cannot edit your own roles' : 'Edit roles')"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Button>
                    <Button
                      v-if="canManageUsers && member.status === 'active'"
                      size="sm"
                      variant="ghost"
                      @click="confirmSuspendMember(member)"
                      :disabled="member.is_subscriber || member.is_you"
                      :title="member.is_subscriber ? 'Cannot suspend subscriber' : (member.is_you ? 'Cannot suspend yourself' : 'Suspend member')"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </Button>
                    <Button
                      v-else-if="canManageUsers && member.status === 'suspended'"
                      size="sm"
                      variant="ghost"
                      @click="reactivateMember(member)"
                      title="Reactivate member"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </Button>
                    <Button
                      v-if="canDeleteUsers"
                      size="sm"
                      variant="danger"
                      @click="confirmRemoveMember(member)"
                      :disabled="member.is_subscriber || member.is_you"
                      :title="member.is_subscriber ? 'Cannot remove subscriber' : (member.is_you ? 'Cannot remove yourself' : 'Remove member')"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="p-6 text-center text-gray-500 dark:text-gray-400">
            <svg class="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <p>No team members yet</p>
          </div>
        </div>
      </Card>

      <!-- Pending Invitations -->
      <Card v-if="invitations && invitations.length > 0">
        <template #header>
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
              Pending Invitations
            </h3>
            <Badge variant="warning">{{ invitations.length }}</Badge>
          </div>
        </template>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Email
                </th>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Roles
                </th>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Invited By
                </th>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Invited
                </th>
                <th class="text-left py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Expires
                </th>
                <th class="text-right py-3 px-6 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr
                v-for="invitation in invitations"
                :key="invitation.id"
                class="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <td class="py-4 px-6 text-sm font-medium text-gray-900 dark:text-gray-100">
                  {{ invitation.email }}
                </td>
                <td class="py-4 px-6">
                  <div class="flex flex-wrap gap-1">
                    <Badge
                      v-for="role in invitation.roles"
                      :key="role"
                      size="sm"
                      variant="secondary"
                    >
                      {{ formatRole(role) }}
                    </Badge>
                  </div>
                </td>
                <td class="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                  {{ invitation.invited_by?.full_name || 'N/A' }}
                </td>
                <td class="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                  {{ formatRelativeDate(invitation.created_at) }}
                </td>
                <td class="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                  {{ formatRelativeDate(invitation.expires_at) }}
                </td>
                <td class="py-4 px-6">
                  <div class="flex justify-end gap-2">
                    <Button
                      v-if="canInviteUsers"
                      size="sm"
                      variant="ghost"
                      @click="resendInvitation(invitation)"
                      :disabled="resendingInvitation === invitation.id"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </Button>
                    <Button
                      v-if="canInviteUsers"
                      size="sm"
                      variant="danger"
                      @click="confirmCancelInvitation(invitation)"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </Button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <!-- Invite Modal -->
      <Modal :show="showInviteModal" @close="closeInviteModal" size="lg" title="Invite Team Member">
        <form @submit.prevent="sendInvitation" class="space-y-6">
            <div class="grid grid-cols-2 gap-4">
              <Input
                v-model="inviteForm.first_name"
                label="First Name"
                type="text"
                :error="inviteForm.errors.first_name"
                required
                placeholder="John"
              />
              <Input
                v-model="inviteForm.last_name"
                label="Last Name"
                type="text"
                :error="inviteForm.errors.last_name"
                required
                placeholder="Doe"
              />
            </div>

            <Input
              v-model="inviteForm.email"
              label="Email Address"
              type="email"
              :error="inviteForm.errors.email"
              required
              placeholder="john.doe@example.com"
            />

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-3">
                Roles <span class="text-red-500">*</span>
              </label>
              <div class="max-h-80 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div v-for="(group, groupIndex) in groupedRolesForInvite" :key="groupIndex" :class="{ 'mt-4': groupIndex > 0 }">
                  <!-- Section Header -->
                  <div class="flex items-center mb-3">
                    <div class="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                    <h3
                      class="px-3 text-xs font-semibold uppercase tracking-wider"
                      :class="group.title === 'System & Administration' ? 'text-red-600 dark:text-red-500' : 'text-gray-500 dark:text-gray-400'"
                    >
                      {{ group.title }}
                    </h3>
                    <div class="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                  </div>

                  <!-- Roles in this section -->
                  <div class="space-y-2">
                    <label
                      v-for="role in group.roles"
                      :key="role.id"
                      class="flex items-start p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        :value="role.id"
                        v-model="inviteForm.role_ids"
                        class="mt-1 mr-3 rounded text-blue-600 focus:ring-blue-500"
                      />
                      <div class="flex-1">
                        <span class="font-medium text-gray-900 dark:text-gray-100">{{ role.name }}</span>
                        <p v-if="role.description" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {{ role.description }}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
              <p v-if="inviteForm.errors.role_ids" class="mt-1 text-sm text-red-600 dark:text-red-400">
                {{ inviteForm.errors.role_ids }}
              </p>
            </div>

            <Input
              v-model="inviteForm.message"
              label="Personal Message (Optional)"
              type="textarea"
              :error="inviteForm.errors.message"
              :rows="3"
              placeholder="Add a personal note to the invitation..."
            />

            <div class="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                @click="closeInviteModal"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                :disabled="inviteForm.processing"
              >
                <svg v-if="inviteForm.processing" class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ inviteForm.processing ? 'Sending...' : 'Send Invitation' }}
              </Button>
            </div>
          </form>
      </Modal>

      <!-- Edit Member Modal -->
      <Modal :show="showEditModal" @close="closeEditModal" size="lg" title="Edit Member">
        <div v-if="selectedMember" class="flex items-center space-x-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mb-6">
          <Avatar
            :src="selectedMember.profile_photo_url"
            :name="selectedMember.full_name"
            size="md"
          />
          <div>
            <p class="font-medium text-gray-900 dark:text-gray-100">{{ selectedMember.full_name }}</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">{{ selectedMember.email }}</p>
          </div>
        </div>

        <!-- Tabs -->
        <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav class="flex -mb-px">
            <button
              @click="editTab = 'personal'"
              :class="[
                'px-4 py-2 text-sm font-medium border-b-2',
                editTab === 'personal'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              ]"
            >
              Personal Information
            </button>
            <button
              @click="editTab = 'roles'"
              :class="[
                'px-4 py-2 text-sm font-medium border-b-2',
                editTab === 'roles'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              ]"
            >
              Roles & Permissions
            </button>
          </nav>
        </div>

        <!-- Personal Information Tab -->
        <form v-show="editTab === 'personal'" @submit.prevent="updatePersonalInfo" class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              v-model="personalInfoForm.first_name"
              label="First Name"
              required
              :error="personalInfoForm.errors.first_name"
            />
            <Input
              v-model="personalInfoForm.last_name"
              label="Last Name"
              required
              :error="personalInfoForm.errors.last_name"
            />
          </div>

          <Input
            v-model="personalInfoForm.email"
            label="Email"
            type="email"
            required
            :error="personalInfoForm.errors.email"
          />

          <Input
            v-model="personalInfoForm.phone"
            label="Phone"
            type="tel"
            :error="personalInfoForm.errors.phone"
          />

          <div class="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              @click="closeEditModal"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              :disabled="personalInfoForm.processing"
            >
              {{ personalInfoForm.processing ? 'Updating...' : 'Update Information' }}
            </Button>
          </div>
        </form>

        <!-- Roles Tab -->
        <form v-show="editTab === 'roles'" @submit.prevent="updateMemberRoles" class="space-y-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-3">
              Roles <span class="text-red-500">*</span>
            </label>
            <div class="max-h-80 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div v-for="(group, groupIndex) in groupedRolesForEdit" :key="groupIndex">
                <!-- Section Header -->
                <div class="flex items-center mb-3" :class="{ 'mt-6': groupIndex > 0 }">
                  <div class="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                  <h3
                    class="px-3 text-xs font-semibold uppercase"
                    :class="group.title === 'System & Administration' ? 'text-red-600 dark:text-red-500' : 'text-gray-500 dark:text-gray-400'"
                  >
                    {{ group.title }}
                  </h3>
                  <div class="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                </div>

                <!-- Roles in this section -->
                <div class="space-y-2">
                  <label
                    v-for="role in group.roles"
                    :key="role.id"
                    class="flex items-start p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      :value="role.id"
                      v-model="editForm.role_ids"
                      class="mt-1 mr-3 rounded text-blue-600 focus:ring-blue-500"
                    />
                    <div class="flex-1">
                      <span class="font-medium text-gray-900 dark:text-gray-100">{{ role.name }}</span>
                      <p v-if="role.description" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {{ role.description }}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
            <p v-if="editForm.errors.role_ids" class="mt-1 text-sm text-red-600 dark:text-red-400">
              {{ editForm.errors.role_ids }}
            </p>
          </div>

          <div class="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              @click="closeEditModal"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              :disabled="editForm.processing"
            >
              {{ editForm.processing ? 'Updating...' : 'Update Roles' }}
            </Button>
          </div>
        </form>
      </Modal>

      <!-- Confirmation Modals -->
      <ConfirmModal
        :show="confirmAction.show"
        :title="confirmAction.title"
        :message="confirmAction.message"
        :confirm-text="confirmAction.confirmText"
        :confirm-variant="confirmAction.variant"
        :processing="confirmAction.processing"
        @confirm="executeConfirmAction"
        @cancel="cancelConfirmAction"
      />
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useForm, router, Link, Head, usePage } from '@inertiajs/vue3';
import route from '@/Utils/route';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Badge from '@/Components/Common/Badge.vue';
import Alert from '@/Components/Common/Alert.vue';
import Input from '@/Components/Common/Input.vue';
import Avatar from '@/Components/Common/Avatar.vue';
import Modal from '@/Components/Common/Modal.vue';
import ConfirmModal from '@/Components/Common/ConfirmModal.vue';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
  members: {
    type: Array,
    default: () => [],
  },
  invitations: {
    type: Array,
    default: () => [],
  },
  availableRoles: {
    type: Array,
    default: () => [],
  },
  usageLimit: {
    type: [Number, String],
    default: 'unlimited',
  },
  usageCurrent: {
    type: Number,
    default: 0,
  },
});

const { success, error } = useToast();
const page = usePage();

// Check permissions
const permissions = computed(() => page.props.auth?.permissions || []);
const canInviteUsers = computed(() => permissions.value.includes('system.users.invite'));
const canViewUsers = computed(() => permissions.value.includes('system.users.view'));
const canManageUsers = computed(() => permissions.value.includes('system.users.manage'));
const canDeleteUsers = computed(() => permissions.value.includes('system.users.delete'));

// Get active modules from subscription
const activeModules = computed(() => page.props.activeModules || []);

// Search and filter state
const searchQuery = ref('');
const statusFilter = ref('');
let searchTimeout = null;

const showInviteModal = ref(false);
const showEditModal = ref(false);
const selectedMember = ref(null);
const editTab = ref('personal'); // personal or roles
const resendingInvitation = ref(null);
const confirmAction = ref({
  show: false,
  title: '',
  message: '',
  confirmText: '',
  variant: 'danger',
  action: null,
  processing: false,
});

/**
 * Calculate usage percentage
 */
const usagePercentage = computed(() => {
  if (props.usageLimit === 'unlimited') return 0;
  return (props.usageCurrent / props.usageLimit) * 100;
});

/**
 * Check if at limit
 */
const isAtLimit = computed(() => {
  if (props.usageLimit === 'unlimited') return false;
  return props.usageCurrent >= props.usageLimit;
});

/**
 * Role to module mapping
 * Maps role slugs to their required modules
 */
const roleModuleMapping = {
  // IT Helpdesk roles
  'it_manager': 'it_helpdesk',
  'helpdesk_manager': 'it_helpdesk',
  'helpdesk_agent': 'it_helpdesk',

  // Maintenance roles
  'maintenance_manager': 'maintenance',
  'maintenance_support_manager': 'maintenance',
  'maintenance_team': 'maintenance',

  // Incident roles
  'incident_manager': 'incidents',
  'incident_coordinator': 'incidents',

  // Checklist/Inspection roles
  'inspection_manager': 'checklists',
  'checklist_manager': 'checklists',

  // General roles (always visible)
  'external_request_reviewer': 'general',
  'auditor': 'general',
  'general_user': 'general',

  // System Administration (always visible)
  'system_admin': 'system',
};

/**
 * Check if a role should be shown based on active modules
 */
const shouldShowRole = (role) => {
  // Always hide company subscriber
  if (role.slug === 'company_subscriber') {
    return false;
  }

  // Check if role requires a specific module
  const requiredModule = roleModuleMapping[role.slug];

  // If role doesn't require a specific module, show it
  if (!requiredModule) {
    return true;
  }

  // General and system roles are always visible
  if (requiredModule === 'general' || requiredModule === 'system') {
    return true;
  }

  // Show role only if the required module is active
  return activeModules.value.includes(requiredModule);
};

/**
 * Filter and sort available roles for invitation
 * Removes subscriber role and filters based on active modules
 */
const filteredRolesForInvite = computed(() => {
  return props.availableRoles.filter(role => shouldShowRole(role));
});

/**
 * Filter available roles for editing
 * Same filtering logic as invite, based on active modules
 */
const filteredRolesForEdit = computed(() => {
  return props.availableRoles.filter(role => shouldShowRole(role));
});

/**
 * Group roles by module for better organization
 * Returns roles organized into sections based on their module
 */
const groupedRolesForInvite = computed(() => {
  const groups = [];

  // IT Helpdesk roles
  if (activeModules.value.includes('it_helpdesk')) {
    const itRoles = filteredRolesForInvite.value.filter(role => roleModuleMapping[role.slug] === 'it_helpdesk');
    if (itRoles.length > 0) {
      groups.push({
        title: 'IT Helpdesk',
        roles: itRoles
      });
    }
  }

  // Maintenance roles
  if (activeModules.value.includes('maintenance')) {
    const maintenanceRoles = filteredRolesForInvite.value.filter(role => roleModuleMapping[role.slug] === 'maintenance');
    if (maintenanceRoles.length > 0) {
      groups.push({
        title: 'Maintenance',
        roles: maintenanceRoles
      });
    }
  }

  // Incidents roles
  if (activeModules.value.includes('incidents')) {
    const incidentRoles = filteredRolesForInvite.value.filter(role => roleModuleMapping[role.slug] === 'incidents');
    if (incidentRoles.length > 0) {
      groups.push({
        title: 'Incidents',
        roles: incidentRoles
      });
    }
  }

  // Inspection & Checklists roles
  if (activeModules.value.includes('checklists')) {
    const checklistRoles = filteredRolesForInvite.value.filter(role => roleModuleMapping[role.slug] === 'checklists');
    if (checklistRoles.length > 0) {
      groups.push({
        title: 'Inspection & Checklists',
        roles: checklistRoles
      });
    }
  }

  // General roles (always shown if they exist)
  const generalRoles = filteredRolesForInvite.value.filter(role => roleModuleMapping[role.slug] === 'general');
  if (generalRoles.length > 0) {
    groups.push({
      title: 'General Roles',
      roles: generalRoles
    });
  }

  // System & Administration roles (always shown at the end)
  const systemRoles = filteredRolesForInvite.value.filter(role => roleModuleMapping[role.slug] === 'system');
  if (systemRoles.length > 0) {
    groups.push({
      title: 'System & Administration',
      roles: systemRoles
    });
  }

  return groups;
});

/**
 * Group roles for edit modal
 */
const groupedRolesForEdit = computed(() => {
  const groups = [];

  // IT Helpdesk roles
  if (activeModules.value.includes('it_helpdesk')) {
    const itRoles = filteredRolesForEdit.value.filter(role => roleModuleMapping[role.slug] === 'it_helpdesk');
    if (itRoles.length > 0) {
      groups.push({
        title: 'IT Helpdesk',
        roles: itRoles
      });
    }
  }

  // Maintenance roles
  if (activeModules.value.includes('maintenance')) {
    const maintenanceRoles = filteredRolesForEdit.value.filter(role => roleModuleMapping[role.slug] === 'maintenance');
    if (maintenanceRoles.length > 0) {
      groups.push({
        title: 'Maintenance',
        roles: maintenanceRoles
      });
    }
  }

  // Incidents roles
  if (activeModules.value.includes('incidents')) {
    const incidentRoles = filteredRolesForEdit.value.filter(role => roleModuleMapping[role.slug] === 'incidents');
    if (incidentRoles.length > 0) {
      groups.push({
        title: 'Incidents',
        roles: incidentRoles
      });
    }
  }

  // Inspection & Checklists roles
  if (activeModules.value.includes('checklists')) {
    const checklistRoles = filteredRolesForEdit.value.filter(role => roleModuleMapping[role.slug] === 'checklists');
    if (checklistRoles.length > 0) {
      groups.push({
        title: 'Inspection & Checklists',
        roles: checklistRoles
      });
    }
  }

  // General roles (always shown if they exist)
  const generalRoles = filteredRolesForEdit.value.filter(role => roleModuleMapping[role.slug] === 'general');
  if (generalRoles.length > 0) {
    groups.push({
      title: 'General Roles',
      roles: generalRoles
    });
  }

  // System & Administration roles (always shown at the end)
  const systemRoles = filteredRolesForEdit.value.filter(role => roleModuleMapping[role.slug] === 'system');
  if (systemRoles.length > 0) {
    groups.push({
      title: 'System & Administration',
      roles: systemRoles
    });
  }

  return groups;
});

/**
 * Handle search with debounce
 */
const handleSearch = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  searchTimeout = setTimeout(() => {
    performFilter();
  }, 500); // 500ms debounce
};

/**
 * Handle filter change
 */
const handleFilter = () => {
  performFilter();
};

/**
 * Clear search
 */
const clearSearch = () => {
  searchQuery.value = '';
  performFilter();
};

/**
 * Perform filter/search on backend
 */
const performFilter = () => {
  router.get(
    route('team.index'),
    {
      search: searchQuery.value,
      status: statusFilter.value,
      _company_id: page.props.company_id,
    },
    {
      preserveState: true,
      preserveScroll: true,
      only: ['members'],
    }
  );
};

// Invite form
const inviteForm = useForm({
  first_name: '',
  last_name: '',
  email: '',
  role_ids: [],
  message: '',
});

// Edit form
const editForm = useForm({
  role_ids: [],
});

const personalInfoForm = useForm({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
});

/**
 * Format role slug to display name
 */
const formatRole = (role) => {
  const roles = {
    'super_admin': 'Super Admin',
    'company_admin': 'Admin',
    'company_subscriber': 'Subscriber',
    'company_user': 'User',
    'support_agent': 'Support Agent',
    'department_head': 'Department Head',
  };
  return roles[role] || role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Get role badge variant
 */
const getRoleVariant = (role) => {
  const variants = {
    'super_admin': 'danger',
    'company_admin': 'primary',
    'company_subscriber': 'success',
    'company_user': 'secondary',
    'support_agent': 'info',
    'department_head': 'warning',
  };
  return variants[role] || 'secondary';
};

/**
 * Format status
 */
const formatStatus = (status) => {
  const statuses = {
    'active': 'Active',
    'suspended': 'Suspended',
    'invited': 'Invited',
    'inactive': 'Inactive',
  };
  return statuses[status] || status;
};

/**
 * Get status badge variant
 */
const getStatusVariant = (status) => {
  const variants = {
    'active': 'success',
    'suspended': 'danger',
    'invited': 'warning',
    'inactive': 'secondary',
  };
  return variants[status] || 'secondary';
};

/**
 * Format relative date
 */
const formatRelativeDate = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} weeks ago`;
  return new Date(dateString).toLocaleDateString();
};

/**
 * Open invite modal
 */
const openInviteModal = () => {
  if (isAtLimit.value) {
    error('You have reached your team member limit. Please upgrade your plan.');
    return;
  }
  showInviteModal.value = true;
};

/**
 * Close invite modal
 */
const closeInviteModal = () => {
  showInviteModal.value = false;
  inviteForm.reset();
  inviteForm.clearErrors();
};

/**
 * Send invitation
 */
const sendInvitation = () => {
  inviteForm.transform((data) => ({
    ...data,
    _company_id: page.props.company_id
  })).post(route('team.invite'), {
    preserveScroll: true,
    onSuccess: () => {
      success('Invitation sent successfully');
      closeInviteModal();
    },
    onError: (errors) => {
      error('Failed to send invitation. Please check the form for errors.');
    },
  });
};

/**
 * Resend invitation
 */
const resendInvitation = (invitation) => {
  resendingInvitation.value = invitation.id;
  router.post(
    route('team.invitation.resend', invitation.id),
    { _company_id: page.props.company_id },
    {
      preserveScroll: true,
      onSuccess: () => {
        success('Invitation resent successfully');
        resendingInvitation.value = null;
      },
      onError: () => {
        error('Failed to resend invitation');
        resendingInvitation.value = null;
      },
    }
  );
};

/**
 * Confirm cancel invitation
 */
const confirmCancelInvitation = (invitation) => {
  confirmAction.value = {
    show: true,
    title: 'Cancel Invitation',
    message: `Are you sure you want to cancel the invitation for ${invitation.email}?`,
    confirmText: 'Yes, Cancel Invitation',
    variant: 'danger',
    action: () => cancelInvitation(invitation),
    processing: false,
  };
};

/**
 * Cancel invitation
 */
const cancelInvitation = (invitation) => {
  confirmAction.value.processing = true;
  router.delete(route('team.invitation.cancel', invitation.id), {
    data: { _company_id: page.props.company_id },
    preserveScroll: true,
    onSuccess: () => {
      success('Invitation cancelled successfully');
      cancelConfirmAction();
    },
    onError: () => {
      error('Failed to cancel invitation');
      confirmAction.value.processing = false;
    },
  });
};

/**
 * Edit member roles
 */
const editMemberRoles = (member) => {
  selectedMember.value = member;
  editTab.value = 'personal'; // Start with personal info tab

  // Populate role form
  editForm.role_ids = member.role_ids || [];

  // Populate personal info form
  personalInfoForm.first_name = member.first_name || '';
  personalInfoForm.last_name = member.last_name || '';
  personalInfoForm.email = member.email || '';
  personalInfoForm.phone = member.phone || '';

  showEditModal.value = true;
};

/**
 * Close edit modal
 */
const closeEditModal = () => {
  showEditModal.value = false;
  selectedMember.value = null;
  editTab.value = 'personal';
  editForm.reset();
  editForm.clearErrors();
  personalInfoForm.reset();
  personalInfoForm.clearErrors();
};

/**
 * Update personal information
 */
const updatePersonalInfo = () => {
  if (!selectedMember.value) return;

  personalInfoForm.transform((data) => ({
    ...data,
    _company_id: page.props.company_id
  })).put(route('team.member.personal-info', selectedMember.value.id), {
    preserveScroll: true,
    onSuccess: () => {
      success('Member information updated successfully');
      closeEditModal();
    },
    onError: () => {
      error('Failed to update member information');
    },
  });
};

/**
 * Update member roles
 */
const updateMemberRoles = () => {
  if (!selectedMember.value) return;

  editForm.transform((data) => ({
    ...data,
    _company_id: page.props.company_id
  })).put(route('team.member.roles', selectedMember.value.id), {
    preserveScroll: true,
    onSuccess: () => {
      success('Member roles updated successfully');
      closeEditModal();
    },
    onError: () => {
      error('Failed to update member roles');
    },
  });
};

/**
 * Confirm suspend member
 */
const confirmSuspendMember = (member) => {
  confirmAction.value = {
    show: true,
    title: 'Suspend Member',
    message: `Are you sure you want to suspend ${member.full_name}? They will lose access to the system until reactivated.`,
    confirmText: 'Yes, Suspend Member',
    variant: 'warning',
    action: () => suspendMember(member),
    processing: false,
  };
};

/**
 * Suspend member
 */
const suspendMember = (member) => {
  confirmAction.value.processing = true;
  router.post(
    route('team.member.status', member.id),
    { status: 'suspended', _company_id: page.props.company_id },
    {
      preserveScroll: true,
      onSuccess: () => {
        success('Member suspended successfully');
        cancelConfirmAction();
      },
      onError: () => {
        error('Failed to suspend member');
        confirmAction.value.processing = false;
      },
    }
  );
};

/**
 * Reactivate member
 */
const reactivateMember = (member) => {
  router.post(
    route('team.member.status', member.id),
    { status: 'active', _company_id: page.props.company_id },
    {
      preserveScroll: true,
      onSuccess: () => {
        success('Member reactivated successfully');
      },
      onError: () => {
        error('Failed to reactivate member');
      },
    }
  );
};

/**
 * Confirm remove member
 */
const confirmRemoveMember = (member) => {
  confirmAction.value = {
    show: true,
    title: 'Remove Member',
    message: `Are you sure you want to remove ${member.full_name} from the team? This action cannot be undone.`,
    confirmText: 'Yes, Remove Member',
    variant: 'danger',
    action: () => removeMember(member),
    processing: false,
  };
};

/**
 * Remove member
 */
const removeMember = (member) => {
  confirmAction.value.processing = true;
  router.delete(route('team.member.remove', member.id), {
    data: { _company_id: page.props.company_id },
    preserveScroll: true,
    onSuccess: () => {
      success('Member removed successfully');
      cancelConfirmAction();
    },
    onError: () => {
      error('Failed to remove member');
      confirmAction.value.processing = false;
    },
  });
};

/**
 * Execute confirm action
 */
const executeConfirmAction = () => {
  if (confirmAction.value.action) {
    confirmAction.value.action();
  }
};

/**
 * Cancel confirm action
 */
const cancelConfirmAction = () => {
  confirmAction.value = {
    show: false,
    title: '',
    message: '',
    confirmText: '',
    variant: 'danger',
    action: null,
    processing: false,
  };
};
</script>
